import * as React from 'react';
import {useMemo} from 'react';
import {Platform, View, ScrollView} from 'react-native';
import {Props} from './model';
import Screen from '../../shared/components/screen';
import {CustomScrollView} from '../../shared/components/scrollView';
import {useStyles} from '../../shared/hooks/styles';
import {DeviceSize, useDeviceSize} from '../../shared/theme/helpers';
import {Breadcrumbs} from '../../shared/components/breadcrumbs';
import {Routes, Screens} from '../../shared/routing';
import {AcceptorDataCard} from './components/acceptorDataCard';
import {Html} from '../../shared/components/html';
import {AcceptorGeoMap} from './components/acceptorGeoMap';
import {AcceptorCard} from './components/acceptorCard';
import {AcceptorResponsePayload} from '../../shared/models/api';
import {BreadcrumbItem} from '../../shared/components/breadcrumbs/model';
import {ResponsiveView} from '../../shared/components/responsiveView';
import {Text} from '../../shared/components/text';
import {LoyaltyAcceptorsList} from '../loyaltyProgram/components/acceptorsList';
import {LoadableContent} from '../../shared/components/loadableContent';
import {useSelector} from 'react-redux';
import {themeSelector} from '../../redux/selectors/runtime/theme';
import {VisImpStatus} from '../../shared/models/visuallyImpairedSettings';
import {AcceptorsFilter} from '../../shared/components/acceptorsFilter';
import {Button} from '../../shared/components/button';
import {useNavigation} from '@react-navigation/native';
import {AcceptorAccordion} from './components/acceptorAccordion';

const getRoutes = (acceptor: AcceptorResponsePayload): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: 'Возможности карты',
      routeName: Screens.CardServices,
      webRoute: Routes.cardService,
    },
    {
      title: 'Программа лояльности',
      routeName: Screens.LoyaltyProgram,
      webRoute: Routes.loyaltyProgram,
    },
  ];
  if (acceptor) {
    breadcrumbs.push({
      title: acceptor.name ?? '',
      routeName: Screens.LoyaltyProgramAcceptor,
      webRoute: Routes.loyaltyProgramAcceptor,
    });
  }
  return breadcrumbs;
};

export default ({acceptor, acceptorBranches, filter, onFilterApply, id}: Props) => {
  const navigation = useNavigation();
  const deviceSize = useDeviceSize();
  const themeSettings = useSelector(themeSelector());
  const isVisuallyImpairedSettingActive = themeSettings.visuallyImpaired === VisImpStatus.Active;
  const styles = useStyles(({mediaOnly, theme, mediaExclude}) => ({
    screen: {},
    sectionContainer: {
      ...mediaOnly(DeviceSize.mobile, {paddingHorizontal: 16, paddingBottom: 50}, {}),
      ...mediaOnly(DeviceSize.tablet, {paddingHorizontal: 35, paddingBottom: 50}, {}),
      ...mediaOnly(DeviceSize.desktop, {paddingHorizontal: 60, paddingBottom: 100}, {}),
      marginTop: 32,
      backgroundColor: theme.screen.container[deviceSize].backgroundColor,
    },
    acceptorDescription: {
      color: theme.color.textColor,
      ...mediaOnly(DeviceSize.desktop, {...theme.fonts.h5Light}, {...theme.fonts.additionalText}),
    },
    acceptorLayout: mediaOnly(DeviceSize.desktop, {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginTop: 38,
    }),
    acceptorLayoutASide: {
      marginBottom: 20,
      ...mediaOnly(DeviceSize.desktop, {
        flexBasis: 315,
        flexShrink: 0,
        flexGrow: 0,
        marginRight: 20,
      }),
    },
    acceptorLayoutMain: mediaOnly(DeviceSize.desktop, {
      flexShrink: 1,
      flexGrow: 1,
    }),
    dataCardWrapper: {
      marginBottom: 20,
    },
    dataCardGeomapWrapper: mediaOnly(
      DeviceSize.desktop,
      {
        marginBottom: 80,
      },
      {
        marginBottom: 40,
      },
    ),
    header: {
      position: 'relative',
      zIndex: 1,
      ...mediaOnly(
        DeviceSize.desktop,
        {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 45,
        },
        {
          marginBottom: 24,
        },
      ),
    },
    headerTitle: mediaOnly(DeviceSize.desktop, {marginRight: 24}, {marginBottom: 24}),
    acceptorsButtonContainer: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      ...mediaOnly(DeviceSize.mobile, {}, {paddingHorizontal: 10}),
    },
    acceptorsFilterButtonMobile: {
      width: '100%',
      marginTop: 10,
      marginBottom: 24,
      marginLeft: 0,
      marginRight: 0,
      ...mediaExclude(DeviceSize.mobile, {maxWidth: 328}),
    },
    loyalityHeader: {
      marginTop: 30,
      fontFamily: 'GothamPro-Medium',
      ...mediaExclude(DeviceSize.mobile, theme.fonts.h2Light),
      ...mediaOnly(DeviceSize.mobile, theme.fonts.action),
    },
    accordionWrap: {
      backgroundColor: theme.color.white,
    },
  }));
  const routes = useMemo(() => getRoutes(acceptor), [acceptor]);
  const scrollRef = React.useRef<ScrollView | null>(null);

  React.useEffect(() => {
    if (Platform.OS === 'web') {
      window.scrollTo(0, 0);
    } else {
      scrollRef.current?.scrollTo({x: 0, y: 0, animated: false});
    }
  }, [id]);

  return (
    <Screen style={styles.screen} key={Platform.OS === 'web' ? id : ''}>
      <CustomScrollView contentInsetAdjustmentBehavior='automatic' ref={scrollRef}>
        <LoadableContent loading={!acceptor}>
          <View style={styles.sectionContainer}>
            <Breadcrumbs routes={routes} />
            <View style={styles.acceptorLayout}>
              <View style={styles.acceptorLayoutASide}>
                <AcceptorCard acceptor={acceptor} />
              </View>
              <View style={styles.acceptorLayoutMain}>
                <View style={styles.dataCardWrapper}>
                  <AcceptorDataCard headerText={acceptor?.name ?? ''}>
                    <Html content={acceptor?.descriptionFull} testID='acceptor-description' />
                  </AcceptorDataCard>
                </View>
                {acceptor?.loyaltyProgramDescription ? (
                  <AcceptorAccordion title='Программа лояльности'>
                    <Html content={acceptor?.loyaltyProgramDescription} testID='acceptor-loyaltyProgramDescription' />
                  </AcceptorAccordion>
                ) : null}
                {/* todo: понять откуда это, если не будет понятно вырезать, было в дизайне */}
                {/*<View style={styles.dataCardWrapper}>*/}
                {/*  <AcceptorDataCard headerText='Как получить' isCloseable={true}>*/}
                {/*    <Text style={styles.acceptorDescription}>*/}
                {/*      Единую социальную карту Свердловской области можно оформить в отделениях банков-партнеров, а также в*/}
                {/*      отделениях МФЦ и Почты России. Для этого достаточно посетить отделение один раз. Готовую карту вы*/}
                {/*      получите уже через 15 минут. Для оформления необходимо предоставить следующие документы: - документ,*/}
                {/*      удостоверяющий личность (паспорт гражданина РФ/удостоверение военнослужащего/военный билет) -*/}
                {/*      страховое свидетельство обязательно пенсионного страхования (СНИЛС) - полис обязательного*/}
                {/*      медицинского страхования (за исключением военнослужащих и приравненных к ним в организации оказания*/}
                {/*      медицинской помощи лиц) - документы, подтверждающие право на получение региональных льгот при их*/}
                {/*      наличии.*/}
                {/*    </Text>*/}
                {/*  </AcceptorDataCard>*/}
                {/*</View>*/}
              </View>
            </View>
            <View style={styles.dataCardGeomapWrapper}>
              {acceptorBranches && <AcceptorGeoMap acceptor={acceptor} acceptorBranches={acceptorBranches} />}
            </View>
            <View>
              <View style={styles.header}>
                <View style={styles.headerTitle}>
                  <ResponsiveView only={[DeviceSize.desktop]}>
                    <Text testID='more-loyalty-partenrs-id' type='h2'>
                      Еще партнеры программы лояльности
                    </Text>
                  </ResponsiveView>
                  <ResponsiveView exclude={[DeviceSize.desktop]}>
                    <Text testID='loyalty-partenrs-id' type='h6'>
                      Партнеры программы лояльности
                    </Text>
                  </ResponsiveView>
                </View>
                {!isVisuallyImpairedSettingActive && deviceSize !== DeviceSize.mobile && (
                  <AcceptorsFilter onFilterApply={onFilterApply} />
                )}
              </View>
              <ResponsiveView exclude={[DeviceSize.mobile]}>
                {isVisuallyImpairedSettingActive && <AcceptorsFilter visuallyImpaired onFilterApply={onFilterApply} />}
              </ResponsiveView>
              <ResponsiveView only={[DeviceSize.mobile]}>
                <View style={styles.acceptorsButtonContainer}>
                  <Button
                    id='loyalty-program-category'
                    style={styles.acceptorsFilterButtonMobile}
                    type='secondary'
                    text='Категория'
                    onPress={() => navigation.navigate(Screens.AcceptorsFilter)}
                  />
                </View>
              </ResponsiveView>
              <LoyaltyAcceptorsList filter={filter} currentAcceptorId={Number(id)} />
            </View>
          </View>
        </LoadableContent>
      </CustomScrollView>
    </Screen>
  );
};
